import type { Fb } from '~/composables/useFirebase'
import { RUN_STORAGE_KEY } from '~/composables/useRun'
import { onAppStateChange } from '~/composables/useAppLifecycle'
import type { RunState } from '~/types/game'

/**
 * ☁️ Cross-device RUN sync (v3.60, per Javi: "start a run on my phone,
 * finish it on the PC").
 *
 * useCloudSync deliberately left runs out — its merge is grow-only
 * (numbers take the max, lists union), which is what makes meta progress
 * conflict-free, and a run is nothing like that. So runs get their own
 * document and their own policy:
 *
 *   users/{uid}/saves/current   { run: <json string>, rev, deviceId, updatedAt }
 *
 * Why a separate doc: the run is ~11KB and writes on every checkpoint;
 * keeping it out of the meta doc means run writes never rewrite meta and
 * can never endanger that grow-only merge.
 *
 * Why a JSON STRING rather than a nested object: Firestore rejects
 * `undefined` and nested arrays, and RunState has both (cleared rider
 * fields, planned-path strokes). Stringifying sidesteps the whole class
 * and matches exactly what localStorage already stores.
 *
 * CONFLICT POLICY lives in utils/runSyncPolicy (resolveSync) so it can be
 * tested without dragging the Firestore SDK into a node bundle. Short
 * version: push when only this device moved, adopt when only the other
 * did, and ASK when both did — silently picking a side is how somebody
 * loses an hour of play.
 *
 * Timers need no special handling: mission clocks are stored values that
 * only tick while the encounter page runs, so a run picked up on another
 * device resumes exactly where it was left — no wall-clock catch-up.
 */

const REV_KEY = 'elemental-dispatch-run-rev'
const BASE_KEY = 'elemental-dispatch-run-base'
const DEVICE_KEY = 'elemental-dispatch-device-id'
/** 👤 v7.15: the account the rev/base counters were last agreed with. */
const IDENTITY_KEY = 'elemental-dispatch-run-identity'
/** 👤 v7.18: a real account's run, PARKED on this device while another
 * real account uses it (`prefix:uid`) - restored when it signs back in. */
const PARKED_KEY = 'elemental-dispatch-run-parked'
const PUSH_DEBOUNCE_MS = 5000

export interface RunSummary {
  act: number
  hp: number
  maxHp: number
  phase: string
  roster: number
  when: number | null
  device: string
}

export interface RunConflict {
  local: RunSummary
  remote: RunSummary
  /** null when the other device ENDED the run (v6.37 tombstone). */
  remoteRun: RunState | null
  remoteRev: number
  /**
   * 🪦 v6.38: false when THIS device ended the run — its tombstone push
   * was CAS-refused because the other device had moved on, so the
   * chooser opens with nothing local to keep. `local` is then a summary
   * of nothing (`Act 0 · 0/0 HP`), and the chooser must not render it.
   */
  hasLocalRun: boolean
}

function readInt(key: string): number {
  if (typeof localStorage === 'undefined') return 0
  return Number(localStorage.getItem(key) ?? 0) || 0
}

function writeInt(key: string, value: number) {
  if (typeof localStorage !== 'undefined') localStorage.setItem(key, String(value))
}

interface AgreedIdentity {
  uid: string
  anonymous: boolean
  /** 👤 v7.18: the counters were checked against this account's cloud rev
   * (a record written by v7.15-v7.17 labelled whatever counters it found). */
  verified?: boolean
}

function readIdentity(): AgreedIdentity | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(IDENTITY_KEY)
    const parsed = raw ? JSON.parse(raw) as Partial<AgreedIdentity> : null
    return parsed && typeof parsed.uid === 'string'
      ? { uid: parsed.uid, anonymous: parsed.anonymous === true, verified: parsed.verified === true }
      : null
  } catch {
    return null
  }
}

interface ParkedRun {
  run: string | null
  rev: number
  base: number
  lastActiveMode?: 'daily' | 'tutorial'
  epoch: number
}

const parkedKey = (uid: string) => `${PARKED_KEY}:${uid}`

function readParked(uid: string): ParkedRun | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(parkedKey(uid))
    const parsed = raw ? JSON.parse(raw) as Partial<ParkedRun> : null
    if (!parsed || Number(parsed.epoch ?? 0) < PROGRESS_EPOCH) return null
    return {
      run: typeof parsed.run === 'string' ? parsed.run : null,
      rev: Number(parsed.rev ?? 0) || 0,
      base: Number(parsed.base ?? 0) || 0,
      lastActiveMode: parsed.lastActiveMode === 'daily' || parsed.lastActiveMode === 'tutorial' ? parsed.lastActiveMode : undefined,
      epoch: PROGRESS_EPOCH,
    }
  } catch {
    return null
  }
}

function writeIdentity(id: AgreedIdentity) {
  if (typeof localStorage !== 'undefined') localStorage.setItem(IDENTITY_KEY, JSON.stringify(id))
}

function deviceId(): string {
  if (typeof localStorage === 'undefined') return 'unknown'
  let id = localStorage.getItem(DEVICE_KEY)
  if (!id) {
    id = `${navigator.platform || 'device'}-${Math.random().toString(36).slice(2, 8)}`
    localStorage.setItem(DEVICE_KEY, id)
  }
  return id
}

export function summarizeRun(run: RunState | null, when: number | null, device: string): RunSummary {
  return {
    act: run?.act ?? 0,
    hp: run?.hp ?? 0,
    maxHp: run?.maxHp ?? 0,
    phase: run?.phase ?? 'none',
    roster: run?.roster?.length ?? 0,
    when,
    device,
  }
}

/** The chooser's state, so app.vue can render it from anywhere. */
export function useRunConflict() {
  return useState<RunConflict | null>('run-conflict', () => null)
}

/**
 * MODULE scope on purpose. useRunSync() is called from the persistence
 * plugin and again from initRunSync; per-closure timers would leave each
 * caller with its own `busy` flag, so muting writes while adopting a
 * remote run would not mute the plugin's — and the adopted run would
 * immediately bump its rev and push straight back over the cloud copy.
 */
let pushTimer: ReturnType<typeof setTimeout> | null = null
let busy = false
/**
 * 🎲 v6.33: the mode of the run this device last had in hand. MODULE
 * scope for the same reason as the two above — the persistence plugin
 * and initRunSync hold separate closures, and the v5.54 original lived
 * inside useRunSync(), so the guard that protects the campaign save had
 * no memory across them. It is also written from noteProgress now, not
 * only from push(): pushes are suppressed for the whole weekly, so
 * push() was frequently never called to set it at all.
 */
let lastActiveMode: 'daily' | 'tutorial' | undefined
/**
 * ☁️ v6.35: the pull in flight, if any. `busy` is a single flag, so two
 * overlapping pulls (the auth watcher and a visibilitychange land
 * together) had the first to finish clear the second's mute — a
 * non-reentrant lock used reentrantly. Callers now join the flight.
 */
let pulling: Promise<void> | null = null
/** 👤 v7.15: whose pull that is - a flight for another account is not joined. */
let pullingUid: string | null = null
/**
 * 👤 v7.15 (code review): bumped on every identity change. A pull or push
 * that was in flight for the previous account checks it after each await
 * and drops its answer - a late read of account A's cloud run must never
 * be adopted into account B's session, nor A's counters be stamped on B.
 */
let identityGen = 0
/**
 * 👤 v7.18 (follow-up review #4): the mute for an identity change - the
 * mutation itself and the watcher flush it causes, cleared a macrotask
 * later, always. v7.15 borrowed `busy` for this, which the new account's
 * first pull then kept up across its network wait, so a checkpoint made
 * during a slow read right after a switch went uncounted.
 */
let switching = false
/**
 * 👤 v7.18 (follow-up review #5): the identity record was written without
 * checking the counters (or is missing, on a device from before v7.15).
 * The next pull checks them against the cloud's rev before trusting them.
 */
let provenanceUnverified = false
/**
 * 🏆 v6.38: up while adopt() is writing run.value on the cloud's behalf.
 * The firebase plugin's abandon watcher reads every run→null as "the
 * player gave up HERE" and submits the old run to the leaderboard. Once
 * a tombstone (v6.37) can null the run from the other side, the device
 * that never played the ending was reporting its stale copy. Raised
 * BEFORE the ref moves and lowered with `busy` a macrotask later: Vue
 * flushes watchers on a microtask, so the watcher always lands inside.
 * Deliberately NOT `busy` itself — that is up for the whole pull, and
 * a Give Up landing while getDoc is in flight is a genuine abandon.
 */
let adopting = false

/** Is the sync layer writing the run ref right now? (v6.38) */
export function adoptingFromCloud(): boolean {
  return adopting
}
/**
 * ☁️ v6.38: the push in flight, if any, and the push asked for while it
 * was flying. Two pushes could be open at once (the debounced tombstone
 * and a tab-hide flush): if the newer committed first, Firestore's RETRY
 * of the older re-ran a callback whose closure still said 'delete',
 * passed the CAS because base had just advanced, and wrote the stone
 * over the new run at the same rev. Pushes now queue behind the flight
 * the way pulls do; `force` survives the join (keepLocal must still win).
 */
let pushing: Promise<void> | null = null
let queued: { force: boolean } | null = null

export function useRunSync() {
  const run = useRunState()
  const user = useAuthUser()
  const conflict = useRunConflict()

  const docRef = (fb: Fb, uid: string) => fb.fs.doc(fb.db, 'users', uid, 'saves', 'current')

  /**
   * A checkpoint happened locally: the run moved on from what the cloud
   * has. `clearedMode` is the slot the persistence plugin just wrote or
   * emptied — the only way to know a NULL run was a weekly rather than
   * the campaign ending (v6.33).
   */
  function noteProgress(clearedMode?: 'daily' | 'tutorial') {
    if (busy || switching) return
    lastActiveMode = run.value?.mode ?? clearedMode
    // 🎲 v5.54: DAILY runs stay device-local — pushing one would clobber
    // the cloud's campaign save (one doc, one policy). The board is the
    // daily's cross-device story, not the save. v6.33: the rev must not
    // move either, or flush() (every tab-hide) pushes on its behalf.
    if (!countsAsProgress({ mode: run.value?.mode, clearedMode })) return
    writeInt(REV_KEY, readInt(REV_KEY) + 1)
    // ☁️ v6.35: the rev still counts — flush() must know there is work —
    // but nothing ships while the chooser is open. The player has not
    // chosen yet, and the cloud copy is the thing they are choosing
    // BETWEEN; a background push would answer for them.
    if (conflict.value) return
    schedulePush()
  }

  /**
   * 👤 v7.15 (code review): the rev/base counters record an AGREEMENT
   * with one account's cloud doc, but they were device-global - after an
   * account switch they were compared against a DIFFERENT account's rev,
   * so the next checkpoint could overwrite that account's run (or
   * silently adopt it). initRunSync calls this before every pull.
   *
   *   real account -> another real account: the local run belongs to the
   *     account that just left (signOutUser settles its push first), so
   *     it leaves the device and the counters start from nothing - the
   *     new account is served from ITS cloud copy.
   *   guest -> account (the anonymous link fallback, or a fresh sign-in):
   *     the run the guest played comes along, but nothing has been agreed
   *     with the account's cloud yet - base 0 makes the next pull compare
   *     honestly: seed an empty cloud, adopt when this device holds
   *     nothing, ASK when both sides hold a run.
   */
  function onIdentity(u: { uid: string; anonymous?: boolean }) {
    const prev = readIdentity()
    const next: AgreedIdentity = { uid: u.uid, anonymous: u.anonymous === true, verified: prev?.uid === u.uid && prev.verified === true }
    const changed = !!prev && prev.uid !== next.uid
    // A record that never had its counters checked (or none at all) is
    // checked by the next pull; a change below re-agrees them from nothing.
    provenanceUnverified = !changed && !next.verified
    if (changed) {
      identityGen++
      if (pushTimer) { clearTimeout(pushTimer); pushTimer = null }
      conflict.value = null
      next.verified = true
      if (!prev.anonymous && !next.anonymous) {
        // 👤 v7.18 (follow-up review #3): the previous account's run is
        // PARKED on this device, not deleted. v7.15 assumed signOutUser's
        // settle() had shipped it, but a push can fail (offline) or be
        // refused (the cloud moved on) and settle() resolves either way -
        // the only copy of that progress was thrown away. Parked, it is
        // back the moment that account signs in here again, and the
        // normal pull compares it with the cloud like any other save.
        muteBriefly()
        if (park(prev.uid)) {
          if (run.value && run.value.mode !== 'daily' && run.value.mode !== 'tutorial') run.value = null
          if (typeof localStorage !== 'undefined') localStorage.removeItem(RUN_STORAGE_KEY)
          writeInt(REV_KEY, 0)
          writeInt(BASE_KEY, 0)
          lastActiveMode = undefined
        } else {
          // 👤 v7.19 (v7.18 review #3): the parking write failed (storage
          // full) - nothing is cleared. The run stays live and meets this
          // account's cloud through the resolver (base 0: seed, adopt or
          // ASK), which is explicit where deleting the only copy was not.
          console.warn('Run sync could not park the previous account\'s run - keeping it on this device.')
          writeInt(BASE_KEY, 0)
          if (run.value) writeInt(REV_KEY, Math.max(readInt(REV_KEY), 1))
        }
      } else {
        writeInt(BASE_KEY, 0)
        if (run.value) writeInt(REV_KEY, Math.max(readInt(REV_KEY), 1))
      }
    }
    // 👤 v7.18: the account that comes back finds its run where it left it -
    // v7.19 (v7.18 review #2): only when the device is not holding another
    // campaign (a guest's, say). Otherwise the parked run waits, and every
    // later pull offers it again once the device is free (unparkIfIdle).
    if (!prev || prev.uid !== next.uid) unparkIfIdle(next.uid)
    writeIdentity(next)
  }

  /** The mute for an identity mutation and the watcher flush it causes. */
  function muteBriefly() {
    switching = true
    adopting = true // the abandon watcher must read this as sync work, not a Give Up
    setTimeout(() => { switching = false; adopting = false }, 0)
  }

  /** 👤 v7.19: a parked run returns only onto a free device. */
  function unparkIfIdle(uid: string): boolean {
    if (!readParked(uid)) return false
    const holdsCampaign = (!!run.value && run.value.mode !== 'daily' && run.value.mode !== 'tutorial')
      || (typeof localStorage !== 'undefined' && localStorage.getItem(RUN_STORAGE_KEY) !== null)
    if (holdsCampaign) return false
    unpark(uid)
    return true
  }

  /** 👤 v7.18: the outgoing account's campaign slot and counters, kept under
   * its uid. v7.19: says whether the write landed - the caller clears
   * nothing when it did not. */
  function park(uid: string): boolean {
    if (typeof localStorage === 'undefined') return true
    const slot = localStorage.getItem(RUN_STORAGE_KEY)
    const entry: ParkedRun = { run: slot, rev: readInt(REV_KEY), base: readInt(BASE_KEY), lastActiveMode, epoch: PROGRESS_EPOCH }
    // Nothing to keep: no run, and nothing unshipped (an ended run whose
    // tombstone has not shipped is rev > base with an empty slot - kept).
    if (!slot && entry.rev === entry.base) return true
    try {
      localStorage.setItem(parkedKey(uid), JSON.stringify(entry))
      return true
    } catch {
      return false
    }
  }

  /** 👤 v7.18: restore what park() kept for this account, if anything.
   * v7.19: the backup goes only once the restore has been read. */
  function unpark(uid: string) {
    const parked = readParked(uid)
    if (!parked) return
    let restored: RunState | null = null
    try {
      restored = parked.run
        ? normalizeRun(JSON.parse(parked.run) as Partial<RunState>, useAscension().unlocked.value)
        : null
    } catch (error) {
      console.warn('A parked run could not be read - leaving it parked.', error)
      return
    }
    muteBriefly()
    if (typeof localStorage !== 'undefined') {
      if (parked.run) localStorage.setItem(RUN_STORAGE_KEY, parked.run)
      else localStorage.removeItem(RUN_STORAGE_KEY)
      localStorage.removeItem(parkedKey(uid))
    }
    // Never over a daily or tutorial in progress: the slot is restored
    // either way, the live ref only when it is the campaign's turn.
    if (!run.value || (run.value.mode !== 'daily' && run.value.mode !== 'tutorial')) run.value = restored
    writeInt(REV_KEY, parked.rev)
    writeInt(BASE_KEY, parked.base)
    lastActiveMode = parked.lastActiveMode
  }

  /** 👤 v7.15: ship any unshipped checkpoint NOW and wait for it - the
   * last chance before an account leaves this device. v7.18: says whether
   * it actually landed (the caller cannot tell from push()). */
  async function settle(): Promise<boolean> {
    if (!user.value) return true
    if (pushTimer || readInt(REV_KEY) !== readInt(BASE_KEY)) await push()
    return readInt(REV_KEY) === readInt(BASE_KEY)
  }

  function schedulePush() {
    if (!user.value) return
    if (pushTimer) clearTimeout(pushTimer)
    pushTimer = setTimeout(() => { void push() }, PUSH_DEBOUNCE_MS)
  }

  /**
   * Ship this device's save. `force` is the player answering the chooser
   * with "keep mine": it wins whatever the cloud holds, but still reads
   * the live REMOTE rev inside the transaction rather than trusting the
   * one captured when the chooser opened (v6.35).
   *
   * v6.38: one flight at a time. A call during a flight joins it and
   * is re-run once it lands - and a flight that committed while local
   * progress moved on re-ships on its own, so nothing that happened
   * mid-flight is left at a rev the other device has already agreed.
   */
  function push({ force = false }: { force?: boolean } = {}): Promise<void> {
    if (pushing) {
      queued = { force: force || (queued?.force ?? false) }
      return pushing
    }
    // Through a helper, not `queued = null` inline: TS narrows a module
    // `let` assigned inside this closure, and `queued.force` after the
    // reset is a read of `never` (typecheck gate).
    const takeQueued = () => { const q = queued; queued = null; return q }
    // On a microtask, so `pushing` is assigned before the flight can
    // run: the transaction's first read is synchronous from here, and a
    // push() made inside that window must find the flight to join.
    pushing = Promise.resolve().then(async () => {
      let next: { force: boolean } | null = { force }
      try {
        while (next) {
          // A join made before this iteration began (keepLocal in the
          // deferral window) is merged, never dropped: force survives.
          const pending = takeQueued()
          const moved = await ship(next.force || (pending?.force ?? false))
          // A joined caller wanted its progress shipped: if the flight
          // already carried it, rev == base and there is nothing to do.
          // A CAS refusal leaves rev > base too, but that is the next
          // PULL's to settle - re-running would only be refused again,
          // which is why this is one re-run and never a loop.
          const joined = takeQueued()
          next = joined
            ? (joined.force || readInt(REV_KEY) !== readInt(BASE_KEY) ? joined : null)
            : (moved ? { force: false } : null)
        }
      } finally {
        // Synchronously at loop exit - a `.finally()` on the promise runs
        // a microtask later, and a push() landing in that gap joined a
        // flight that would never look at `queued` again.
        pushing = null
      }
    })
    return pushing
  }

  /** One transaction. Resolves true when local progress landed DURING
   * the flight and the commit did not carry it - the caller re-ships. */
  async function ship(force: boolean): Promise<boolean> {
    const u = user.value
    if (!u || busy) return false
    // 👤 v7.19 (v7.18 review #4): during the brief identity mute the write is
    // deferred, not dropped - the resolver's 'push' for a restored run used
    // to be lost when the cloud read beat the mute's macrotask.
    if (switching) {
      setTimeout(() => { void push() }, 0)
      return false
    }
    // An open chooser owns the cloud doc until the player answers.
    if (conflict.value && !force) return false
    if (pushTimer) { clearTimeout(pushTimer); pushTimer = null }
    const value = run.value
    // Dailies never sync (device-local by design) — and clearing one
    // must not touch the campaign's cloud doc. v6.33: the old guard
    // cleared itself on use, so a SECOND push (the next tab-hide flush)
    // fell straight through and deleted the campaign anyway. It stays
    // set now until a real campaign checkpoint clears it.
    const action = cloudAction({ hasRun: !!value, mode: value?.mode, lastActiveMode })
    if (value?.mode === 'daily') lastActiveMode = 'daily'
    if (action === 'skip') return false
    // 🪦 v6.38: value, action, rev and base are ONE snapshot, taken
    // here. The rev used to be read inside the transaction - after an
    // await, by which time a run started mid-flight had bumped it (push
    // never mutes noteProgress), so the tombstone landed at the NEW
    // run's rev and the run later shipped at the same number, which a
    // device that had adopted the stone read as "nothing moved". And a
    // Firestore retry re-runs the callback: with everything captured,
    // it can only re-write the same move, never a stale action at a
    // newer rev.
    const rev = readInt(REV_KEY)
    const base = readInt(BASE_KEY)
    const body = action === 'delete' ? null : JSON.stringify(value)
    const gen = identityGen
    const fb = await firebaseReady()
    if (gen !== identityGen) return false // 👤 v7.15: the account changed while we waited
    const ref = docRef(fb, u.uid)
    try {
      // ☁️ v6.35: read-then-write in ONE transaction. A blind setDoc
      // could land on top of a save the other device wrote between our
      // last pull and this push — the lost update the rev counters exist
      // to prevent, but never actually checked.
      const written = await fb.fs.runTransaction(fb.db, async (tx) => {
        const snap = await tx.get(ref)
        const remoteRev = snap.exists() ? Number(snap.data()?.rev ?? 0) : 0
        if (!force && remoteRev > base) {
          // The cloud moved past what we last agreed on. Leave it for
          // the next pull to resolve (adopt, or ask the player).
          return null
        }
        // Forcing means outranking whatever is up there RIGHT NOW, not
        // the rev the chooser happened to capture.
        const stamp = force ? Math.max(rev, remoteRev + 1) : rev
        // 🪦 v6.37: when the CAMPAIGN ends (won, lost or abandoned)
        // this writes a TOMBSTONE rather than deleting the doc. A
        // deletion looked identical to "this account has never synced",
        // so the other device read it as a fresh account and uploaded
        // its stale copy back - the ending never travelled. A doc that
        // still carries a rev, with a null run, is a move the resolver
        // can see, so the other device adopts the ending instead.
        //
        // Null, not the STRING 'null': clients on older builds take
        // their existing not-a-string branch and clear correctly, where
        // a string would have reached normalizeRun and thrown. The
        // firestore.rules `run` check was widened to allow it first.
        // (v6.38: that holds for the adopt verdict. An older tab that
        // also played gets its chooser with a zeroed statline for the
        // tombstone side - the data outcome is still right, only the
        // label is off, and it ends when that tab reloads.)
        tx.set(ref, {
          run: body,
          rev: stamp,
          deviceId: deviceId(),
          // 🧨 v3.79: stamped so a save written before a progress wipe
          // can be recognised and dropped rather than adopted.
          epoch: PROGRESS_EPOCH,
          updatedAt: fb.fs.serverTimestamp(),
        })
        return stamp
      })
      if (written === null) {
        console.info('Cloud run moved on — leaving this push for the next pull.')
        return false
      }
      // 👤 v7.15: the write went to the account that asked for it; the
      // counters now belong to another one and must not be stamped.
      if (gen !== identityGen) return false
      // 🪦 v6.38: the counters were set to whatever was written, which
      // LOWERED the rev when progress landed mid-flight and made that
      // progress look already shipped. Base is what the cloud now
      // holds; the rev keeps every checkpoint that arrived during the
      // flight on top of it, so the re-ship outranks the stone.
      //
      // A pull that ADOPTED mid-flight has already aligned both counters
      // to a newer cloud copy; stamping ours on top would leave rev > base
      // over a run this device just took, and the next pull would ask.
      if (readInt(BASE_KEY) !== base) return false
      const moved = Math.max(0, readInt(REV_KEY) - rev)
      writeInt(REV_KEY, written + moved)
      writeInt(BASE_KEY, written)
      return moved > 0
    } catch (error) {
      console.warn('Run sync push failed.', error)
      return false
    }
  }

  /** Flush immediately — used when the tab is closing or backgrounding. */
  function flush() {
    if (!user.value) return
    if (pushTimer || readInt(REV_KEY) !== readInt(BASE_KEY)) void push()
  }

  function pullAndResolve(uid: string) {
    // 🎲 v5.54: never pull WHILE a daily is on screen — adopting the
    // cloud campaign save would clobber the active daily. The pull
    // re-fires on the next foreground/sign-in once the campaign is back.
    if (run.value?.mode === 'daily' || run.value?.mode === 'tutorial') return Promise.resolve()
    // 👤 v7.19: a run parked for this account comes back once the device is free.
    unparkIfIdle(uid)
    // 👤 v7.15: a flight for ANOTHER account is not this one's - it bails
    // on its generation check; this uid gets a pull of its own.
    if (pulling && pullingUid === uid) return pulling
    pullingUid = uid
    pulling = runPull(uid).finally(() => {
      if (pullingUid === uid) { pulling = null; pullingUid = null }
    })
    return pulling
  }

  async function runPull(uid: string) {
    const gen = identityGen
    // ☁️ v6.35: adopt() takes the mute over on its own terms (it clears
    // it a macrotask later, once the watchers have settled). The finally
    // below used to clear it synchronously the moment adopt returned,
    // which is BEFORE Vue flushes — so the adopted run counted as local
    // progress and pushed straight back over the copy it just took.
    let adopted = false
    try {
      const fb = await firebaseReady()
      const snap = await fb.fs.getDoc(docRef(fb, uid))
      // 👤 v7.15: the account changed while this read was in flight - its
      // answer belongs to a session that is gone.
      if (gen !== identityGen) return
      // ⏳ v7.16 (code review): the mute goes up HERE, once the cloud copy is
      // in hand, not before the network wait. Raised across the read, it
      // swallowed genuine checkpoints made during a slow pull (a phone
      // resuming on a weak signal), and the resolver then adopted the
      // cloud over play it had never counted. The counters are read after
      // it, so a checkpoint that landed mid-read is a local move.
      busy = true
      let localRev = readInt(REV_KEY)
      let baseRev = readInt(BASE_KEY)

      // 👤 v7.18 (follow-up review #5): counters from before v7.15 carry no
      // record of whose they are. If the cloud's rev is not the one they
      // claim to have agreed on, the agreement was with some other account
      // (or none): forget it. Unshipped local progress (rev > base) then
      // meets this cloud as an ASK; a fully shipped run steps aside for
      // the account's own copy (adopt / seed), never overwrites it.
      if (provenanceUnverified) {
        provenanceUnverified = false
        // v7.19 (v7.18 review #1): equal rev numbers prove nothing - they are
        // independent per-account counters, and two of them coinciding let
        // a stranger's run through. What does prove the agreement is the
        // cloud doc naming THIS device as its last writer.
        const mine = snap.exists() && String(snap.data()?.deviceId ?? '') === deviceId()
        if (snap.exists() && baseRev !== 0 && !mine) {
          if (localRev === baseRev) {
            localRev = 0
            writeInt(REV_KEY, 0)
          }
          baseRev = 0
          writeInt(BASE_KEY, 0)
        }
        const id = readIdentity()
        if (id && id.uid === uid) writeIdentity({ ...id, verified: true })
      }

      if (!snap.exists()) {
        // Nothing up there yet: seed it from whatever this device holds.
        busy = false
        if (resolveSync({ localRev, baseRev, remoteRev: 0, hasLocalRun: !!run.value, remoteExists: false }) === 'seed') {
          writeInt(REV_KEY, Math.max(localRev, 1))
          await push()
        }
        return
      }

      const data = snap.data()

      // 🧨 v3.79: a run saved before the wipe must NOT come back. The
      // local rev counter was cleared with everything else, so without
      // this the resolver sees localRev 0 against a live remote rev and
      // dutifully adopts a pre-wipe run — the one case where the meta
      // epoch check would not have helped, because runs live in their own
      // document. Delete it and behave as though the cloud were empty.
      if (Number(data.epoch ?? 0) < PROGRESS_EPOCH) {
        console.info('Cloud run predates the current epoch — discarding it.')
        try {
          await fb.fs.deleteDoc(docRef(fb, uid))
        } catch { /* a failed delete just means we discard it again next time */ }
        busy = false
        return
      }

      const remoteRev = Number(data.rev ?? 0)
      // 🪦 v6.37: a tombstone parses to null, and normalizeRun(null)
      // throws on raw.playtimeMs - so guard the PARSE, not the type.
      const parsed = typeof data.run === 'string'
        ? JSON.parse(data.run) as Partial<RunState> | null
        : null
      const remoteRun = parsed
        // 🏦 v4.92 (audit E8): same ascension clamp the local loader applies.
        ? normalizeRun(parsed, useAscension().unlocked.value)
        : null
      const remoteAt = data.updatedAt?.toMillis?.() ?? null
      const remoteDevice = String(data.deviceId ?? 'another device')

      // 🪦 v6.38: a tombstone says "the run we agreed on ended". A
      // device that never agreed on anything (base 0: signed-out play,
      // a fresh install) holding its own run is not the device the
      // stone was written for, so the ending protects nothing from it -
      // and the chooser's "that device finished this run" would describe
      // a run this device never held. Read the stone as an empty cloud
      // and seed, as the deleted doc did before v6.37.
      const strangerToStone = remoteRun === null && baseRev === 0 && !!run.value
      const verdict = resolveSync({
        localRev, baseRev, remoteRev,
        hasLocalRun: !!run.value,
        remoteExists: !strangerToStone,
      })
      if (verdict === 'noop') { busy = false; return }
      if (verdict === 'push') { busy = false; await push(); return }
      if (verdict === 'seed') {
        // Outrank the stone, but agree ITS rev as the base: the push
        // still goes through the CAS, so a live run that lands in the
        // meantime is asked about, not clobbered.
        busy = false
        writeInt(BASE_KEY, remoteRev)
        writeInt(REV_KEY, Math.max(localRev, remoteRev + 1))
        await push()
        return
      }
      // 🪦 v6.38: both devices ended it (this one's stone was refused by
      // the CAS, the other's landed). 'ask' opened a chooser between two
      // nothings; both sides agree, so take the ending. A null run alone
      // cannot say so: a cleared WEEKLY leaves it null with the campaign
      // still in its slot (v6.33), and that campaign is what the chooser
      // is for - so the slot has to be empty too.
      const endedHere = !run.value && typeof localStorage !== 'undefined'
        && localStorage.getItem(RUN_STORAGE_KEY) === null
      if (verdict === 'adopt' || (endedHere && remoteRun === null)) {
        adopted = true
        adopt(remoteRun, remoteRev)
        return
      }
      // 'ask': both sides moved since they last agreed. Do not guess.
      // Cancel any push already on the clock: it was scheduled before we
      // knew there was anything to choose between.
      if (pushTimer) { clearTimeout(pushTimer); pushTimer = null }
      conflict.value = {
        local: summarizeRun(run.value, Date.now(), 'this device'),
        remote: summarizeRun(remoteRun, remoteAt, remoteDevice),
        remoteRun,
        remoteRev,
        hasLocalRun: !!run.value,
      }
    } catch (error) {
      console.warn('Run sync pull failed.', error)
    } finally {
      if (!adopted) busy = false
    }
  }

  /** Take the cloud's run wholesale and align the counters to it. */
  function adopt(remoteRun: RunState | null, remoteRev: number) {
    busy = true
    // 🏆 v6.38: raised before the ref moves, so the abandon watcher can
    // tell a tombstone taken from the cloud from a Give Up. Only for a
    // tombstone: a live adopt never nulls the ref, and keeping the mute
    // down there means a Give Up in the same tick is still reported.
    adopting = remoteRun === null
    run.value = remoteRun
    if (typeof localStorage !== 'undefined') {
      if (remoteRun) localStorage.setItem(RUN_STORAGE_KEY, JSON.stringify(remoteRun))
      else localStorage.removeItem(RUN_STORAGE_KEY)
    }
    writeInt(REV_KEY, remoteRev)
    writeInt(BASE_KEY, remoteRev)
    conflict.value = null
    // Let the watchers settle before local writes count as progress again.
    setTimeout(() => { busy = false; adopting = false }, 0)
  }

  /**
   * Chooser: keep what is on this device and overwrite the cloud.
   * Clearing `conflict` FIRST is load-bearing — it releases the write
   * lock push() honours. v6.35: the rev is settled inside the
   * transaction against the live remote, not against c.remoteRev, which
   * is stale the moment the other device pushes again.
   *
   * 🪦 v6.38: with NO local run this force-writes a tombstone over the
   * other device's live run — on purpose. This device ended the run and
   * its ending lost the race; the chooser's first option now reads "End
   * the run everywhere" rather than "Keep this device's run", so the
   * tombstone is exactly what the player just asked for. The defect was
   * the label, not the write. The v6.33 weekly guard is released for
   * this one answer (see below), or the write would silently skip.
   */
  async function keepLocal() {
    const c = conflict.value
    conflict.value = null
    if (!c) return
    // "End the run everywhere" ends the CAMPAIGN - the only run a chooser
    // can be about (pulls never run during a weekly, and weeklies never
    // move the rev). A weekly cleared since the ending leaves
    // lastActiveMode at 'daily', which would turn this forced push into
    // a v6.33 skip: no tombstone, and the chooser back on the next pull.
    if (!c.hasLocalRun) lastActiveMode = undefined
    await push({ force: true })
  }

  /** Chooser: take the other device's run. */
  function takeRemote() {
    const c = conflict.value
    if (c) adopt(c.remoteRun, c.remoteRev)
  }

  return { noteProgress, schedulePush, push, flush, settle, onIdentity, pullAndResolve, keepLocal, takeRemote, conflict }
}

/**
 * Wired from the firebase plugin, next to initCloudSync: pull on sign-in
 * and whenever the tab comes back to the foreground (the moment a player
 * switching devices would expect to see the other run).
 */
export function initRunSync() {
  const sync = useRunSync()
  const user = useAuthUser()

  watch(user, (u) => {
    if (!u) return
    // 👤 v7.15: re-agree the counters (or clear the other account's run)
    // BEFORE the pull compares anything.
    sync.onIdentity(u)
    void sync.pullAndResolve(u.uid)
  }, { immediate: true })

  if (import.meta.client) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return
      const u = user.value
      if (u) void sync.pullAndResolve(u.uid)
    })
  }
  // 📱 M6: and when the app itself comes back from the background (the
  // native shell). The way out is the save plugin's flush, as on pagehide.
  onAppStateChange((active) => {
    const u = user.value
    if (active && u) void sync.pullAndResolve(u.uid)
  })
}
